import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  Description as DescriptionIcon,
  Assessment as AssessmentIcon,
  Schedule as ScheduleIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import api from '../../api/axios';

interface FacilityInfo {
  ccn: string;
  facility_name: string;
  state?: string;
}

interface OwnerGroupCaseActionSectionProps {
  schemeId: string | null;
  ownerGroupId?: string;
  facilities: FacilityInfo[];
}

const OwnerGroupCaseActionSection: React.FC<OwnerGroupCaseActionSectionProps> = ({ schemeId, ownerGroupId, facilities }) => {
  const [generatingCcn, setGeneratingCcn] = useState<string | null>(null);
  const [costReportGenerating, setCostReportGenerating] = useState(false);
  const [analysisGeneratingCcn, setAnalysisGeneratingCcn] = useState<string | null>(null);
  const [generatingComplaint, setGeneratingComplaint] = useState(false);
  const [generatingMemo, setGeneratingMemo] = useState(false);
  const [generatingExpertReport, setGeneratingExpertReport] = useState(false);

  const downloadDocFromGroupEndpoint = async (
    urlPath: string,
    fallbackName: string,
    setBusy: (b: boolean) => void,
  ) => {
    if (!schemeId || !ownerGroupId) return;
    setBusy(true);
    try {
      const res = await api.post(
        `/schemes/${schemeId}/${urlPath}/`,
        { group_id: ownerGroupId },
        { responseType: 'blob' },
      );
      const disposition = res.headers['content-disposition'] || '';
      const match = disposition.match(/filename="?([^"]+)"?/);
      const filename = match ? match[1] : fallbackName;
      const blob = new Blob([res.data]);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error(`Failed to generate ${urlPath}:`, err);
    } finally {
      setBusy(false);
    }
  };

  const handleGenerateComplaint = () =>
    downloadDocFromGroupEndpoint(
      'generate-owner-group-complaint',
      'Understaffing_Complaint.docx',
      setGeneratingComplaint,
    );

  const handleGenerateProsecutionMemo = () =>
    downloadDocFromGroupEndpoint(
      'generate-owner-group-prosecution-memo',
      'Prosecution_Memo.docx',
      setGeneratingMemo,
    );

  const handleGenerateExpertReport = () =>
    downloadDocFromGroupEndpoint(
      'generate-expert-report',
      'Expert_Report.docx',
      setGeneratingExpertReport,
    );

  const handleGenerateSupplement = async (ccn: string) => {
    if (!schemeId) return;
    setGeneratingCcn(ccn);
    try {
      const res = await api.post(
        `/schemes/${schemeId}/generate-investigative-supplement/`,
        { ccn },
        { responseType: 'blob' },
      );
      const disposition = res.headers['content-disposition'] || '';
      const match = disposition.match(/filename="?([^"]+)"?/);
      const filename = match ? match[1] : `Investigative_Supplement_${ccn}.docx`;
      const blob = new Blob([res.data]);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to generate investigative supplement:', err);
    } finally {
      setGeneratingCcn(null);
    }
  };

  const handleGenerateCostReportAnalysis = async (ccn: string) => {
    if (!schemeId) return;
    setAnalysisGeneratingCcn(ccn);
    try {
      const res = await api.post(`/schemes/${schemeId}/generate-cost-report-analysis/`, { ccn });
      if (res.data?.task_id) {
        const taskId = res.data.task_id;
        const poll = setInterval(async () => {
          try {
            const statusRes = await api.get(`/schemes/${schemeId}/cost-report-analysis-status/?task_id=${taskId}`);
            if (statusRes.data?.state === 'SUCCESS') {
              clearInterval(poll);
              setAnalysisGeneratingCcn(null);
              if (statusRes.data?.download_url) {
                const dlRes = await api.get(statusRes.data.download_url, { responseType: 'blob' });
                const contentType = dlRes.headers['content-type'] || '';
                if (contentType.includes('application/json')) {
                  const text = await (dlRes.data as Blob).text();
                  const json = JSON.parse(text);
                  if (json.url) window.open(json.url, '_blank');
                } else {
                  const disposition = dlRes.headers['content-disposition'] || '';
                  const match = disposition.match(/filename="?([^"]+)"?/);
                  const filename = match ? match[1] : `cost_report_analysis_${ccn}.zip`;
                  const blob = new Blob([dlRes.data]);
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = filename;
                  a.click();
                  URL.revokeObjectURL(url);
                }
              }
            } else if (statusRes.data?.state === 'FAILURE') {
              clearInterval(poll);
              setAnalysisGeneratingCcn(null);
            }
          } catch {
            clearInterval(poll);
            setAnalysisGeneratingCcn(null);
          }
        }, 3000);
      }
    } catch (err) {
      console.error('Failed to generate cost report analysis:', err);
      setAnalysisGeneratingCcn(null);
    }
  };

  const handleGenerateCostReports = async (ccn: string) => {
    if (!schemeId) return;
    setCostReportGenerating(true);
    try {
      const res = await api.post(`/schemes/${schemeId}/generate-cost-reports/`, { ccn });
      if (res.data?.task_id) {
        const taskId = res.data.task_id;
        const poll = setInterval(async () => {
          try {
            const statusRes = await api.get(`/schemes/${schemeId}/cost-reports-status/?task_id=${taskId}`);
            if (statusRes.data?.state === 'SUCCESS') {
              clearInterval(poll);
              setCostReportGenerating(false);
              if (statusRes.data?.download_url) {
                const dlRes = await api.get(statusRes.data.download_url, { responseType: 'blob' });
                const contentType = dlRes.headers['content-type'] || '';
                if (contentType.includes('application/json')) {
                  const text = await (dlRes.data as Blob).text();
                  const json = JSON.parse(text);
                  if (json.url) window.open(json.url, '_blank');
                } else {
                  const disposition = dlRes.headers['content-disposition'] || '';
                  const match = disposition.match(/filename="?([^"]+)"?/);
                  const filename = match ? match[1] : `cost_reports_${ccn}.zip`;
                  const blob = new Blob([dlRes.data]);
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = filename;
                  a.click();
                  URL.revokeObjectURL(url);
                }
              }
            } else if (statusRes.data?.state === 'FAILURE') {
              clearInterval(poll);
              setCostReportGenerating(false);
            }
          } catch {
            clearInterval(poll);
            setCostReportGenerating(false);
          }
        }, 2000);
      }
    } catch (err) {
      console.error('Failed to generate cost reports:', err);
      setCostReportGenerating(false);
    }
  };

  return (
    <Box>
      <Typography variant="h6" fontWeight={700} sx={{ mb: 0.5 }}>
        Case Action
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Generate investigative documents and run case-specific analyses.
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {/* Complaint */}
        <Paper variant="outlined" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 3, py: 2, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, flex: 1, minWidth: 0 }}>
            <Box sx={{ color: 'text.primary', mt: 0.25 }}>
              <DescriptionIcon />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="subtitle1" fontWeight={600}>FCA Complaint</Typography>
              <Typography variant="body2" color="text.secondary">
                Owner-group-scoped complaint with the core data tables (days operating, state-min/RN-coverage failures, related-party entities) populated from platform data.
              </Typography>
            </Box>
          </Box>
          <Button
            variant="outlined"
            size="small"
            disabled={!ownerGroupId || generatingComplaint}
            startIcon={generatingComplaint ? <CircularProgress size={14} color="inherit" /> : undefined}
            onClick={handleGenerateComplaint}
            sx={{ ml: 3, flexShrink: 0, textTransform: 'none', fontSize: '0.78rem' }}
          >
            {generatingComplaint ? 'Generating...' : 'Generate'}
          </Button>
        </Paper>

        {/* Prosecution Memo */}
        <Paper variant="outlined" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 3, py: 2, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, flex: 1, minWidth: 0 }}>
            <Box sx={{ color: 'text.primary', mt: 0.25 }}>
              <AssessmentIcon />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="subtitle1" fontWeight={600}>Prosecution Memo</Typography>
              <Typography variant="body2" color="text.secondary">
                Owner-group-scoped prosecution memo with facility identification, acuity/state-min compliance, related-party totals and matrix, citations/penalties, and RN-LPN wage anomalies.
              </Typography>
            </Box>
          </Box>
          <Button
            variant="outlined"
            size="small"
            disabled={!ownerGroupId || generatingMemo}
            startIcon={generatingMemo ? <CircularProgress size={14} color="inherit" /> : undefined}
            onClick={handleGenerateProsecutionMemo}
            sx={{ ml: 3, flexShrink: 0, textTransform: 'none', fontSize: '0.78rem' }}
          >
            {generatingMemo ? 'Generating...' : 'Generate'}
          </Button>
        </Paper>

        {/* First CID — awaiting document, no generator wired yet */}
        <Paper
          variant="outlined"
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 3, py: 2, borderRadius: 2, opacity: 0.45 }}
        >
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, flex: 1, minWidth: 0 }}>
            <Box sx={{ color: 'text.disabled', mt: 0.25 }}>
              <ScheduleIcon />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="subtitle1" fontWeight={600} color="text.disabled">First CID</Typography>
              <Typography variant="body2" color="text.disabled">
                Draft the initial Civil Investigative Demand (CID) for the owner group.
              </Typography>
            </Box>
          </Box>
          <Box sx={{ ml: 3, flexShrink: 0 }}>
            <Typography variant="caption" color="text.disabled" sx={{ fontStyle: 'italic' }}>
              Coming Soon
            </Typography>
          </Box>
        </Paper>

        {/* Expert Report */}
        <Paper variant="outlined" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 3, py: 2, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, flex: 1, minWidth: 0 }}>
            <Box sx={{ color: 'text.primary', mt: 0.25 }}>
              <AssessmentIcon />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="subtitle1" fontWeight={600}>Expert Report</Typography>
              <Typography variant="body2" color="text.secondary">
                Nurse-staffing expert report (Halifax format) with state-minimum compliance, PBJ-vs-acuity (Harrington/Schnelle) HPRD comparison, and days/value of understaffing tables populated from platform data.
              </Typography>
            </Box>
          </Box>
          <Button
            variant="outlined"
            size="small"
            disabled={!ownerGroupId || generatingExpertReport}
            startIcon={generatingExpertReport ? <CircularProgress size={14} color="inherit" /> : undefined}
            onClick={handleGenerateExpertReport}
            sx={{ ml: 3, flexShrink: 0, textTransform: 'none', fontSize: '0.78rem' }}
          >
            {generatingExpertReport ? 'Generating...' : 'Generate'}
          </Button>
        </Paper>

        {/* Investigative Supplement hidden
        <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
          <Accordion elevation={0} disableGutters sx={{ '&:before': { display: 'none' } }}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{ px: 3, py: 0.5 }}
            >
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, flex: 1, minWidth: 0 }}>
                <Box sx={{ color: 'text.primary', mt: 0.25 }}>
                  <DescriptionIcon />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="subtitle1" fontWeight={600}>
                    Investigative Supplement
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Auto-generate the Case Action memo with staffing records requests, claims data references, and ownership/affiliated persons &mdash; populated from platform data.
                  </Typography>
                </Box>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 2, pt: 1.5, pb: 2 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {facilities.map((fac) => (
                  <Paper
                    key={fac.ccn}
                    variant="outlined"
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      px: 2,
                      py: 1.25,
                      borderRadius: 1.5,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={600} noWrap>{fac.facility_name}</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>{fac.ccn}</Typography>
                    </Box>
                    <Button
                      variant="outlined"
                      size="small"
                      disabled={generatingCcn === fac.ccn}
                      startIcon={generatingCcn === fac.ccn ? <CircularProgress size={14} color="inherit" /> : undefined}
                      onClick={() => handleGenerateSupplement(fac.ccn)}
                      sx={{ ml: 2, flexShrink: 0, textTransform: 'none', fontSize: '0.78rem' }}
                    >
                      {generatingCcn === fac.ccn ? 'Generating...' : 'Generate'}
                    </Button>
                  </Paper>
                ))}
              </Box>
            </AccordionDetails>
          </Accordion>
        </Paper>
        */}

        {/* Disabled "Coming Soon" tools removed (Related Party Cost Report Analysis, Punch Detail Analysis, Medicaid Claims Data Analysis)
        [
          { title: 'Related Party Cost Report Analysis', description: 'Generate cost report analyses for relevant facilities.' },
          { title: 'Punch Detail Analysis', description: 'Upload and analyze facility punch detail data against PBJ submissions.' },
          { title: 'Medicaid Claims Data Analysis', description: 'Match understaffed days against Medicaid claims to quantify billing on days below acuity.' },
        ].map((tool) => (
          <Paper
            key={tool.title}
            variant="outlined"
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 3,
              py: 2,
              borderRadius: 2,
              opacity: 0.45,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, flex: 1, minWidth: 0 }}>
              <Box sx={{ color: 'text.disabled', mt: 0.25 }}>
                <ScheduleIcon />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="subtitle1" fontWeight={600} color="text.disabled">
                  {tool.title}
                </Typography>
                <Typography variant="body2" color="text.disabled">
                  {tool.description}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ ml: 3, flexShrink: 0 }}>
              <Typography variant="caption" color="text.disabled" sx={{ fontStyle: 'italic' }}>
                Coming Soon
              </Typography>
            </Box>
          </Paper>
        )) */}
      </Box>
    </Box>
  );
};

export default OwnerGroupCaseActionSection;
